/**
 * GeometryListItem Component
 *
 * Single item in the geometries list. Supports both edit and view modes.
 * Features: checkbox, inline name editing, action buttons, active state.
 *
 * @module components/map/GeometriesList/core
 */

import { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Checkbox, Text, ActionIcon, Tooltip } from '@mantine/core';
import {
  IconMapPin,
  IconLine,
  IconPolygon,
  IconEdit,
  IconTrash,
} from '@tabler/icons-react';
import { GeometryType } from '../../../../types/geometry';
import { InlineNameEditor } from './InlineNameEditor';
import type { GeometryListItemProps } from '../types';
import { ICON_SIZE_MD } from '../../../../constants/ui';
import { Z_INDEX_TOOLTIP } from '../../../../constants/zIndex';
import classes from '../styles/GeometryListItem.module.css';

/**
 * Get icon component for geometry type
 */
const getGeometryIcon = (type: GeometryType) => {
  const iconProps = { size: ICON_SIZE_MD };
  switch (type) {
    case GeometryType.Marker:
      return <IconMapPin {...iconProps} />;
    case GeometryType.Line:
      return <IconLine {...iconProps} />;
    case GeometryType.Shape:
      return <IconPolygon {...iconProps} />;
    default:
      return <IconMapPin {...iconProps} />;
  }
};

/**
 * GeometryListItem Component
 *
 * Displays a single geometry with appropriate actions based on mode.
 */
export const GeometryListItem = memo(function GeometryListItem({
  geometry,
  mode,
  isSelected = false,
  isChecked = false,
  showCheckbox = false,
  showActions = false,
  onClick,
  onCheckboxChange,
  onDelete,
  onRename,
  onZoomTo,
}: GeometryListItemProps) {
  const { t } = useTranslation();
  const [isEditingName, setIsEditingName] = useState(false);

  // Mode flags
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';

  // Handle double-click to edit name (edit mode only)
  const handleDoubleClick = useCallback(() => {
    if (isEditMode && onRename) {
      setIsEditingName(true);
    }
  }, [isEditMode, onRename]);

  // Handle rename save
  const handleRenameSave = useCallback(
    (newName: string) => {
      if (onRename) {
        onRename(newName);
      }
      setIsEditingName(false);
    },
    [onRename]
  );

  // Handle rename cancel
  const handleRenameCancel = useCallback(() => {
    setIsEditingName(false);
  }, []);

  // Handle item click
  const handleClick = useCallback(() => {
    // Block clicks during inline editing
    if (isEditingName) return;

    // View mode: zoom to geometry (entire row is clickable)
    if (isViewMode && onZoomTo) {
      onZoomTo();
      return;
    }

    // Edit mode: select geometry for editing
    if (onClick) {
      onClick();
    }
  }, [isEditingName, isViewMode, onZoomTo, onClick]);

  return (
    <div
      className={`${classes.container} ${isSelected ? classes.selected : ''}`}
      onClick={handleClick}
      data-view-mode={isViewMode}
    >
      <Group wrap="nowrap" gap="sm" className={classes.content} align="center">
        {/* Checkbox (edit mode only) */}
        {showCheckbox && isEditMode && (
          <div className={classes.checkboxWrapper}>
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                onCheckboxChange?.();
              }}
            />
          </div>
        )}

        {/* Icon */}
        <div className={classes.icon}>{getGeometryIcon(geometry.type)}</div>

        {/* Name and Type */}
        <div className={classes.info} onDoubleClick={handleDoubleClick}>
          {isEditingName && isEditMode ? (
            <InlineNameEditor
              value={geometry.name}
              onSave={handleRenameSave}
              onCancel={handleRenameCancel}
              isActive={isEditingName}
            />
          ) : (
            <>
              <Text size="sm" className={classes.name}>
                {geometry.name}
              </Text>
              {geometry.description && (
                <Tooltip
                  label={geometry.description}
                  zIndex={Z_INDEX_TOOLTIP}
                  events={{ hover: true, focus: true, touch: false }}
                >
                  <Text className={classes.description}>
                    {geometry.description}
                  </Text>
                </Tooltip>
              )}
            </>
          )}
        </div>

        {/* Actions (edit mode only) */}
        {showActions && !isEditingName && isEditMode && (
          <Group gap="xs" className={classes.actions}>
            {/* Edit button - triggers name editing (same as double-click) */}
            {onRename && (
              <Tooltip
                label={t('geometries.actions.edit')}
                zIndex={Z_INDEX_TOOLTIP}
                events={{ hover: true, focus: true, touch: false }}
              >
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditingName(true);
                  }}
                >
                  <IconEdit size={ICON_SIZE_MD} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Delete button */}
            {onDelete && (
              <Tooltip
                label={t('geometries.actions.delete')}
                zIndex={Z_INDEX_TOOLTIP}
                events={{ hover: true, focus: true, touch: false }}
              >
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <IconTrash size={ICON_SIZE_MD} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )}
      </Group>
    </div>
  );
});
